import {
  Component,
  ContentChild,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormGroupDirective,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  MatFormField,
  MatFormFieldAppearance,
  MatFormFieldControl,
} from '@angular/material/form-field';
import { AppSettingService } from '@rd/core/services/app-setting/app-setting.service';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { FormBuilderService } from '../../services/form-builder.service';
import { FormBuilderInputComponentType, InputBaseType } from '../../types';
import { FORM_BUILDER_INPUT_TOKEN } from '../form-builder-input';

@Component({
  selector: 'rd-form-builder-field-wrapper',
  templateUrl: './form-builder-field-wrapper.component.html',
  providers: [
    {
      provide: FORM_BUILDER_INPUT_TOKEN,
      useExisting: FormBuilderFieldWrapperComponent,
      multi: true,
    },
  ],
})
export class FormBuilderFieldWrapperComponent
  implements FormBuilderInputComponentType, OnInit, OnDestroy
{
  @Input() inputProps: InputBaseType;

  matFormFieldAppearance: MatFormFieldAppearance;
  props: InputBaseType;
  formGroup: UntypedFormGroup;
  formControl: AbstractControl;

  isSubmitted$ = new Subject<boolean>();
  destroy$ = new Subject<boolean>();

  @ContentChild(MatFormFieldControl, { static: true })
  public formFieldControl: MatFormFieldControl<any>;

  @ViewChild('materialFormField', { static: true })
  public matFormField: MatFormField;

  constructor(
    public formGroupDirective: FormGroupDirective,
    private appSettingService: AppSettingService,
    private formBuilderService: FormBuilderService,
  ) {}

  ngOnInit(): void {
    this.props = this.formBuilderService.buildInputProps(this.inputProps);
    this.matFormFieldAppearance = this.appSettingService.matFormFieldAppearance;
    this.matFormField._control = this.formFieldControl;
    //
    this.formGroup = this.formGroupDirective.control;
    this.formGroup.registerControl(this.props.key, new UntypedFormControl());
    this.formControl = this.formGroup.controls[this.props.key];

    if (this.props.disabled) {
      this.formControl.disable();
    } else if (this.props.syncValidators) {
      this.formControl.addValidators(
        // @ts-ignore
        this.props.syncValidators,
      );
    }

    this.formControl.updateValueAndValidity();

    // change submit status into
    this.isSubmitted$.pipe(takeUntil(this.destroy$), take(1)).subscribe(() => {
      // check required on Submit
      if (this.inputProps.requiredOnSubmit) {
        this.formControl.addValidators(Validators.required);
        this.formControl.updateValueAndValidity();
        this.formControl.markAsTouched();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  isEmpty(): boolean {
    return this.validateAfterFormIsTouched(
      () => !!this.formControl.hasError('required'),
    );
  }

  isInvalidPattern(): boolean {
    return this.validateAfterFormIsTouched(
      () => !!this.formControl.hasError('pattern'),
    );
  }

  get inputLabelStyleName(): string | null {
    return this.props.className ? this.props.className + '-label' : null;
  }

  get inputErrorEmptyStyleName(): string | null {
    return this.props.className ? this.props.className + '-empty' : null;
  }

  get inputErrorInvalidStyleName(): string | null {
    return this.props.className ? this.props.className + '-invalid' : null;
  }

  // Must ensure form is touched already, as specs fail unless this check is present
  private validateAfterFormIsTouched(validateFn: () => boolean): boolean {
    return !!this.formControl.touched && validateFn();
  }
}
